'use client';

import PhilosophyHero from './PhilosophyHero';
import ManifestoSection from './ManifestoSection';
import DesignPrinciples from './DesignPrinciples';
import ArtOfLight from './ArtOfLight';
import CollaborationStories from './CollaborationStories';
import PhilosophyInPractice from './PhilosophyInPractice';
import SeasonalPhilosophy from './SeasonalPhilosophy';
import PhilosophyCTA from './PhilosophyCTA';

interface ManifestoPoint {
  title: string;
  description: string;
  image: string;
  alt: string;
}

interface Principle {
  icon: string;
  title: string;
  description: string;
}

interface TimeOfDay {
  time: string;
  title: string;
  description: string;
  image: string;
  alt: string;
}

interface Collaboration {
  name: string;
  role: string;
  quote: string;
  project: string;
  image: string;
  alt: string;
}

interface CaseStudy {
  title: string;
  location: string;
  challenge: string;
  solution: string;
  outcome: string;
  image: string;
  alt: string;
}

interface Season {
  name: string;
  title: string;
  description: string;
  colorPalette: string[];
  image: string;
  alt: string;
}

const DesignPhilosophyInteractive = () => {
  const manifestoPoints: ManifestoPoint[] = [
  {
    title: "Curtains as Architecture",
    description: "We reject the notion of curtains as mere decoration. At ARZERA, each piece is conceived as an architectural element—a vertical plane that defines space, controls light, and creates atmosphere. Like walls and columns, our curtains shape how you experience a room, transforming ordinary windows into portals of possibility.",
    image: "https://images.unsplash.com/photo-1723257133428-fc8f16185cf0",
    alt: "Elegant floor-to-ceiling ivory curtains framing tall windows in minimalist modern living room with natural light"
  },
  {
    title: "The Poetry of Light",
    description: "Light is the soul of interior design, and curtains are its choreographers. We design each collection to orchestrate the interplay between natural illumination and shadow, creating dynamic spaces that evolve throughout the day. From the soft glow of morning to the dramatic silhouettes of evening, ARZERA curtains transform light into living art.",
    image: "https://images.unsplash.com/photo-1528822855841-e8bf3134cdc9",
    alt: "Sheer white curtains filtering golden afternoon sunlight creating soft shadows on hardwood floor"
  },
  {
    title: "Timeless Over Trendy",
    description: "Fashion fades, but true design endures. We eschew fleeting trends in favor of timeless elegance that transcends seasons and decades. Our aesthetic draws from classical proportions, natural materials, and enduring silhouettes—creating curtains that remain as relevant and beautiful twenty years from now as they are today.",
    image: "https://images.unsplash.com/photo-1601677367900-de57a6ef20ab",
    alt: "Classic taupe linen curtains with subtle pleating in traditional elegant living room with antique furniture"
  },
  {
    title: "Craftsmanship as Foundation",
    description: "Every ARZERA curtain begins in the hands of master artisans who have dedicated their lives to the craft. We honor traditional techniques—hand-stitched hems, precision pleating, meticulous fabric selection—while embracing modern innovations that enhance durability and performance. This marriage of heritage and innovation ensures each piece is an heirloom in the making.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9bd44e1-1765368308629.png",
    alt: "Close-up of artisan hands carefully stitching champagne silk curtain fabric with precision and detail"
  }];


  const principles: Principle[] = [
  {
    icon: "SparklesIcon",
    title: "Quiet Luxury",
    description: "True elegance whispers. Our designs embody understated sophistication—no logos, no ostentation, just impeccable quality that speaks for itself."
  },
  {
    icon: "ScaleIcon",
    title: "Proportional Harmony",
    description: "Every element is calibrated to achieve visual balance. From fabric weight to pleat depth, we obsess over proportions that feel inherently right."
  },
  {
    icon: "SwatchIcon",
    title: "Material Integrity",
    description: "We source only the finest natural fibers—European linens, Italian silks, Belgian velvets—materials that age gracefully and feel luxurious to the touch."
  },
  {
    icon: "CogIcon",
    title: "Functional Beauty",
    description: "Aesthetics and performance are inseparable. Our curtains control light, insulate spaces, and reduce noise while maintaining visual poetry."
  },
  {
    icon: "PuzzlePieceIcon",
    title: "Contextual Design",
    description: "No two spaces are identical. We design with architectural context in mind, ensuring each curtain complements its environment rather than competing with it."
  },
  {
    icon: "ClockIcon",
    title: "Enduring Quality",
    description: "We build for generations, not seasons. Every seam, every hem, every detail is executed to withstand decades of daily use while maintaining its beauty."
  }];


  const timesOfDay: TimeOfDay[] = [
  {
    time: "Morning",
    title: "Dawn's Gentle Awakening",
    description: "As morning light filters through ARZERA curtains, spaces come alive with soft, diffused illumination. Our fabrics are engineered to transform harsh morning sun into a gentle glow that energizes without overwhelming—creating the perfect atmosphere for peaceful mornings and productive beginnings.",
    image: "https://images.unsplash.com/photo-1614510150619-9803bb9dbe19",
    alt: "Soft morning sunlight filtering through sheer ivory curtains illuminating peaceful bedroom with warm glow"
  },
  {
    time: "Midday",
    title: "Controlled Brilliance",
    description: "During peak daylight hours, our curtains become sophisticated light modulators. Whether fully drawn to create intimate cocoons or partially open to frame views, ARZERA designs give you complete control over brightness and privacy—maintaining comfort without sacrificing connection to the outside world.",
    image: "https://images.unsplash.com/photo-1614241963694-e645d95242b2",
    alt: "Partially drawn taupe curtains controlling bright midday sunlight in modern living room with balanced illumination"
  },
  {
    time: "Afternoon",
    title: "Golden Hour Poetry",
    description: "As afternoon transitions to evening, ARZERA curtains capture the magic of golden hour. Our carefully selected fabrics interact with warm, angled light to create depth and dimension—casting subtle shadows and highlights that transform ordinary rooms into atmospheric sanctuaries.",
    image: "https://images.unsplash.com/photo-1713377504389-bb998ed115dc",
    alt: "Golden afternoon light creating dramatic shadows through floor-length curtains in elegant dining room"
  },
  {
    time: "Evening",
    title: "Twilight Elegance",
    description: "As natural light fades, our curtains create the perfect backdrop for evening ambiance. They soften artificial lighting, add warmth to interiors, and provide privacy without feeling closed off—establishing the serene, sophisticated atmosphere essential for relaxation and entertaining.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_111cde97c-1764904751340.png",
    alt: "Luxurious champagne curtains glowing softly with interior lighting during twilight in sophisticated living space"
  }];


  const collaborations: Collaboration[] = [
  {
    name: "Elena Marchetti",
    role: "Principal Architect, Marchetti Studio",
    quote: "ARZERA understands that curtains are not afterthoughts—they're integral architectural elements. Their ability to translate our spatial concepts into fabric form is unparalleled. Every project we've collaborated on has been elevated by their craftsmanship and design sensitivity.",
    project: "Riverside Penthouse, Manhattan",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e4f4bd99-1764786603645.png",
    alt: "Professional female architect in modern office reviewing curtain fabric samples and architectural drawings"
  },
  {
    name: "James Thornton",
    role: "Interior Designer, Thornton Interiors",
    quote: "Working with ARZERA is like partnering with artists who happen to specialize in textiles. They bring a level of thoughtfulness and precision to window treatments that transforms entire rooms. Their curtains don't just hang—they perform.",
    project: "Coastal Estate, Hamptons",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b8b1118f-1764651350791.png",
    alt: "Distinguished male interior designer in elegant suit examining luxury curtain fabrics in design studio"
  }];


  const caseStudies: CaseStudy[] = [
  {
    title: "Metropolitan Art Collector's Loft",
    location: "SoHo, New York",
    challenge: "A 3,500 sq ft industrial loft with 14-foot ceilings and floor-to-ceiling windows required window treatments that would control intense southern light without diminishing the dramatic architectural volume or obstructing views of the Manhattan skyline.",
    solution: "We designed custom motorized curtains in a specially woven Belgian linen that filters 70% of UV rays while maintaining transparency. The fabric's subtle texture adds warmth to the industrial space, and the motorized system allows precise light control throughout the day.",
    outcome: "The curtains transformed the loft from a challenging, overlit space into a sophisticated gallery environment. The client reports that artwork is now properly protected from sun damage, while the space maintains its airy, open character. The motorized system has become an essential part of their daily routine.",
    image: "https://images.unsplash.com/photo-1706931175440-b52e527600ad",
    alt: "Spacious industrial loft with floor-to-ceiling sheer curtains filtering natural light over modern art collection"
  },
  {
    title: "Historic Brownstone Restoration",
    location: "Boston, Massachusetts",
    challenge: "A 1890s brownstone undergoing meticulous restoration needed period-appropriate window treatments that would honor the home's heritage while meeting modern performance standards for insulation and light control.",
    solution: "We created custom curtains using traditional construction methods—hand-sewn French pleats, weighted hems, and interlining—but executed in contemporary performance fabrics that provide superior insulation and UV protection. The color palette was carefully researched to match the home's original aesthetic.",
    outcome: "The curtains seamlessly blend with the restored interiors, appearing authentically period while delivering 21st-century performance. The homeowners have noted a 30% reduction in heating costs and complete elimination of sun damage to their antique furnishings.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19643fa39-1766427916827.png",
    alt: "Elegant Victorian-era room with traditional pleated curtains in warm taupe complementing restored period details"
  }];


  const seasons: Season[] = [
  {
    name: "Spring",
    title: "Renewal & Light",
    description: "Spring design embraces renewal and optimism. We favor lighter fabrics in soft, nature-inspired hues—pale greens, warm ivories, and gentle blush tones. Sheer layers become prominent, celebrating the return of longer days and softer light. This season is about opening spaces to possibility while maintaining the refined elegance that defines ARZERA.",
    colorPalette: ["#F8F6F0", "#E8F4E8", "#FFF5F0", "#F0F4E8"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c114498a-1766335078939.png",
    alt: "Light and airy room with pale green sheer curtains billowing gently in spring breeze with fresh flowers"
  },
  {
    name: "Summer",
    title: "Luminous Serenity",
    description: "Summer design focuses on managing abundant light while maintaining connection to the outdoors. We emphasize breathable linens and lightweight silks in cool, sophisticated neutrals. The goal is to create spaces that feel fresh and serene even during the hottest days—using fabric technology to block heat while preserving views and natural illumination.",
    colorPalette: ["#FEFCF8", "#E8E2D5", "#F2EDE3", "#D4E8E8"],
    image: "https://images.unsplash.com/photo-1636719680622-e57215d0f84c",
    alt: "Bright summer living room with flowing white linen curtains and cool neutral tones creating serene atmosphere"
  },
  {
    name: "Autumn",
    title: "Warmth & Depth",
    description: "Autumn design introduces richer textures and deeper tones. We layer heavier fabrics—velvets, wools, and substantial linens—in warm taupes, soft bronzes, and muted terracottas. This season is about creating cozy, enveloping spaces that provide comfort as days shorten, while maintaining the sophisticated restraint that prevents spaces from feeling heavy or dark.",
    colorPalette: ["#E8E2D5", "#C4915C", "#A67C7C", "#8B9A5B"],
    image: "https://images.unsplash.com/photo-1587071529495-5c44c106306d",
    alt: "Cozy autumn interior with rich bronze velvet curtains and warm taupe accents creating inviting atmosphere"
  },
  {
    name: "Winter",
    title: "Intimate Elegance",
    description: "Winter design prioritizes warmth, insulation, and intimate atmosphere. We favor the heaviest fabrics in our collection—interlined silks, wool blends, and layered treatments that provide both thermal performance and acoustic dampening. Color palettes lean toward deep charcoals, warm ivories, and subtle champagne golds—creating sophisticated cocoons that feel luxurious and protective.",
    colorPalette: ["#2C2C2C", "#F8F6F0", "#D4AF37", "#6B6B6B"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ab62b762-1764927249733.png",
    alt: "Luxurious winter bedroom with heavy layered curtains in deep charcoal and champagne creating warm intimate space"
  }];


  return (
    <>
      <PhilosophyHero data-component-id="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx:230:6" data-component-path="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx" data-component-line="230" data-component-end-line="232" data-component-file="DesignPhilosophyInteractive.tsx" data-component-name="PhilosophyHero" data-component-content="%7B%22elementName%22%3A%22PhilosophyHero%22%7D"
        title="Design Philosophy"
        subtitle="Where architectural vision meets textile artistry—transforming windows into sculptural elements that define and elevate interior atmospheres" />

      <ManifestoSection data-component-id="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx:234:6" data-component-path="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx" data-component-line="234" data-component-end-line="234" data-component-file="DesignPhilosophyInteractive.tsx" data-component-name="ManifestoSection" data-component-content="%7B%22elementName%22%3A%22ManifestoSection%22%7D" manifestoPoints={manifestoPoints} />
      <DesignPrinciples data-component-id="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx:235:6" data-component-path="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx" data-component-line="235" data-component-end-line="235" data-component-file="DesignPhilosophyInteractive.tsx" data-component-name="DesignPrinciples" data-component-content="%7B%22elementName%22%3A%22DesignPrinciples%22%7D" principles={principles} />
      <ArtOfLight data-component-id="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx:236:6" data-component-path="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx" data-component-line="236" data-component-end-line="236" data-component-file="DesignPhilosophyInteractive.tsx" data-component-name="ArtOfLight" data-component-content="%7B%22elementName%22%3A%22ArtOfLight%22%7D" timesOfDay={timesOfDay} />
      <CollaborationStories data-component-id="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx:237:6" data-component-path="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx" data-component-line="237" data-component-end-line="237" data-component-file="DesignPhilosophyInteractive.tsx" data-component-name="CollaborationStories" data-component-content="%7B%22elementName%22%3A%22CollaborationStories%22%7D" collaborations={collaborations} />
      <PhilosophyInPractice data-component-id="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx:238:6" data-component-path="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx" data-component-line="238" data-component-end-line="238" data-component-file="DesignPhilosophyInteractive.tsx" data-component-name="PhilosophyInPractice" data-component-content="%7B%22elementName%22%3A%22PhilosophyInPractice%22%7D" caseStudies={caseStudies} />
      <SeasonalPhilosophy data-component-id="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx:239:6" data-component-path="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx" data-component-line="239" data-component-end-line="239" data-component-file="DesignPhilosophyInteractive.tsx" data-component-name="SeasonalPhilosophy" data-component-content="%7B%22elementName%22%3A%22SeasonalPhilosophy%22%7D" seasons={seasons} />
      <PhilosophyCTA data-component-id="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx:240:6" data-component-path="src/app/design-philosophy/components/DesignPhilosophyInteractive.tsx" data-component-line="240" data-component-end-line="240" data-component-file="DesignPhilosophyInteractive.tsx" data-component-name="PhilosophyCTA" data-component-content="%7B%22elementName%22%3A%22PhilosophyCTA%22%7D" />
    </>);

};

export default DesignPhilosophyInteractive;